import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { normalizeQuestionOptions } from "@/lib/checkout-form";
import { cn } from "@/lib/utils";
import { panelDialog, panelPill } from "@/components/producer/panel";
import { NATIVE_ATTENDEE_FIELDS, parseOptions, type TierPreview } from "./types";

export function FormPreviewModal({
  isOpen,
  onClose,
  questions,
  tiers,
}: {
  isOpen: boolean;
  onClose: () => void;
  questions: any[];
  tiers: TierPreview[];
}) {
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const setAnswer = (id: string, value: any) => setAnswers((prev) => ({ ...prev, [id]: value }));

  const orderQuestions = questions.filter((q) => q.applies_to !== "attendee");
  const attendeeQuestions = questions.filter((q) => q.applies_to === "attendee");

  const renderField = (question: any) => {
    const rawOptions = parseOptions(question.options);
    const opts = Array.isArray(rawOptions) ? normalizeQuestionOptions(rawOptions) : [];
    const value = answers[question.id];

    switch (question.field_type) {
      case "textarea":
        return (
          <Textarea
            placeholder="Sua resposta..."
            className="max-w-md bg-background"
            rows={3}
            value={value || ""}
            onChange={(e) => setAnswer(question.id, e.target.value)}
          />
        );
      case "select":
        return (
          <Select value={value || ""} onValueChange={(v) => setAnswer(question.id, v)}>
            <SelectTrigger className="max-w-md bg-background">
              <SelectValue placeholder="Selecione uma opção" />
            </SelectTrigger>
            <SelectContent>
              {opts.map((opt: string, i: number) => (
                <SelectItem key={i} value={opt || "opt-" + i}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "date":
        return (
          <Input
            type="date"
            className="max-w-md bg-background"
            value={value || ""}
            onChange={(e) => setAnswer(question.id, e.target.value)}
          />
        );
      case "radio":
        return (
          <RadioGroup className="space-y-2.5 mt-2" value={value || ""} onValueChange={(v) => setAnswer(question.id, v)}>
            {opts.map((opt: string, i: number) => (
              <div key={i} className="flex items-center gap-2">
                <RadioGroupItem value={opt} id={`fp-radio-${question.id}-${i}`} />
                <Label htmlFor={`fp-radio-${question.id}-${i}`} className="text-sm font-normal cursor-pointer">{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      case "checkbox": {
        const checkedList: string[] = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2.5 mt-2">
            {opts.map((opt: string, i: number) => {
              const checked = checkedList.includes(opt);
              return (
                <div key={i} className="flex items-center gap-2">
                  <Checkbox
                    id={`fp-chk-${question.id}-${i}`}
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = c ? [...checkedList, opt] : checkedList.filter((o) => o !== opt);
                      setAnswer(question.id, next);
                    }}
                  />
                  <label htmlFor={`fp-chk-${question.id}-${i}`} className="text-sm font-normal cursor-pointer">{opt}</label>
                </div>
              );
            })}
          </div>
        );
      }
      default:
        return (
          <Input
            placeholder="Sua resposta..."
            className="max-w-md bg-background"
            value={value || ""}
            onChange={(e) => setAnswer(question.id, e.target.value)}
          />
        );
    }
  };

  const renderGroup = (list: any[]) =>
    list.map((question) => {
      const assignedTierNames = (question.tier_ids || [])
        .map((tierId: string) => tiers.find((tier) => tier.id === tierId)?.name)
        .filter(Boolean);
      return (
        <div key={question.id} className="bg-background rounded-xl border shadow-sm p-5 space-y-3">
          <Label className="text-base text-foreground font-medium flex items-start gap-1.5 break-words whitespace-pre-wrap leading-normal">
            <span>{question.question}</span>
            {question.is_required && <span className="text-destructive shrink-0">*</span>}
          </Label>
          {renderField(question)}
          {assignedTierNames.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              Aparece apenas para: <span className="text-primary/80 font-medium">{assignedTierNames.join(", ")}</span>
            </p>
          )}
        </div>
      );
    });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent mobilePresentation="fullscreen" className={cn(panelDialog, "max-w-2xl")}>
        <div className="flex flex-col h-[85vh] max-h-[700px]">
          <div className="px-6 py-4 border-b bg-background flex items-center justify-between shrink-0">
            <div>
              <h2 className="font-display text-lg font-semibold tracking-tight">Preview do formulário</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Simulação interativa de como o participante verá as perguntas no checkout.
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-8 min-h-0">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-muted-foreground">
                Dados do participante (nativos)
              </p>
              <div className="space-y-3">
                {NATIVE_ATTENDEE_FIELDS.map((field) => {
                  const Icon = field.icon;
                  return (
                    <div
                      key={field.id}
                      className="bg-muted/30 rounded-xl border border-dashed border-border/70 p-5 space-y-3 opacity-90"
                    >
                      <Label className="text-base text-muted-foreground font-medium flex items-start gap-1.5">
                        <Icon className="w-4 h-4 mt-0.5 shrink-0 opacity-70" />
                        <span>{field.label}</span>
                        <span className="text-destructive/70 shrink-0">*</span>
                      </Label>
                      <Input
                        disabled
                        placeholder={field.placeholder}
                        className="max-w-md bg-muted/50 text-muted-foreground border-dashed"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {orderQuestions.length > 0 && (
              <div className="space-y-4">
                <p className="text-xs font-semibold text-muted-foreground">Perguntas do pedido (1x)</p>
                {renderGroup(orderQuestions)}
              </div>
            )}
            {attendeeQuestions.length > 0 && (
              <div className="space-y-4">
                <p className="text-xs font-semibold text-muted-foreground">Perguntas extras por ingresso</p>
                {renderGroup(attendeeQuestions)}
              </div>
            )}
            {questions.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhuma pergunta extra cadastrada — só os campos nativos acima.
              </p>
            )}
          </div>

          <div className="p-4 border-t bg-background flex items-center justify-end shrink-0">
            <Button variant="outline" onClick={onClose} className={cn("h-9", panelPill)}>Fechar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
